import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { ApiService } from '../pages/services/api.service';
import { NotificationService } from '../pages/services/notification.service';
import { GET_TWO_FACTOR_AUTH_STATUS } from '../config';
import { TwoFactorAuthDialogComponent } from '../pages/components/two-factor-auth-dialog/two-factor-auth-dialog.component';

export type TwoFactorAuthType = 'otp' | 'ga' | 'disabled' | '';

export type TwoFactorAction =
  | 'change_password'
  | 'set_report_password'
  | 'delete_account'
  | 'api_key_view'
  | 'api_key_generate'
  | 'api_key_update'
  | 'api_key_disable'
  | 'api_key_delete'
  | 'sms_peid_manage'
  | 'sms_api_password_manage'
  | 'sms_pii_hashing'
  | 'sms_content_storage'
  | 'sms_link_tracking'
  | 'sms_api_access_security'
  | 'sms_sender_id_management'
  | 'sms_duplicate_check'
  | 'sms_multilingual'
  | 'profile_edit'
  // non-whitelisted actions (2FA bypassed when authType === 'disabled')
  | 'whatsapp_pii_hashing'
  | 'whatsapp_content_storage'
  | 'whatsapp_auto_reply'
  | 'whatsapp_add_user'
  | 'whatsapp_integrations_add'
  | 'whatsapp_integrations_remove'
  | 'webhook_manage'
  | 'link_tracking'
  | 'soip_integration'
  | 'mmlite_toggle'
  | 'profile_twofa_settings'
  | 'mail_rcpt_encryption'
  | 'rcs_recipient_pii_hashing'
  | 'subaccount_status_toggle'
  | 'subaccount_delete'
  | 'subaccount_manage_credit'
  | 'subaccount_recurring_toggle'
  | 'subaccount_recurring_save'
  | 'smtp_integration_password_reset'
  | 'smtp_relay_show_password'
  | 'smtp_relay_allowed_ips_edit'
  | 'scheduled_report_manage'
  | 'sender_email_authorization'
  | 'domain_delete';

export interface TwoFactorAuthStatus {
  authType: TwoFactorAuthType;
  email: string | null;
  validTill: string | null;
}

interface TwoFactorAuthBootstrapResponse {
  status: string;
  message?: string;
  data?: {
    auth_type?: TwoFactorAuthType;
    email?: string | null;
    valid_till?: string | null;
  };
  error_info?: { message?: string };
}

@Injectable({ providedIn: 'root' })
export class TwoFactorAuthService {
  private readonly _status$ = new BehaviorSubject<TwoFactorAuthStatus>({
    authType: '',
    email: null,
    validTill: null,
  });

  readonly status$ = this._status$.asObservable();

  private bootstrapRequest$?: Observable<boolean>;

  constructor(
    private readonly apiService: ApiService,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog
  ) {}

  get snapshot(): TwoFactorAuthStatus {
    return this._status$.value;
  }

  /**
   * Call after token/session is available (e.g., after GET_PROFILE_INFO success).
   * Safe to call multiple times; it will reuse the in-flight request.
   */
  loadFromServer(): Observable<boolean> {
    if (!this.bootstrapRequest$) {
      this.bootstrapRequest$ = this.apiService.get(GET_TWO_FACTOR_AUTH_STATUS).pipe(
        map((res: TwoFactorAuthBootstrapResponse) => {
          if (res?.status !== 'success') {
            const message = res?.error_info?.message ?? res?.message ?? 'Unable to load two-factor status.';
            this.notificationService.showError(message);
            return false;
          }

          const authType = res?.data?.auth_type ?? this.snapshot.authType;
          const email = res?.data?.email ?? null;
          const validTill = res?.data?.valid_till ?? null;

          this.setStatus({ authType, email, validTill });
          return true;
        }),
        catchError((err: any) => {
          console.error('Error loading two-factor status:', err);
          this.notificationService.showError('Unable to load two-factor status.');
          this.setStatus({ authType: '', email: null, validTill: null });
          return of(false);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }

    return this.bootstrapRequest$;
  }

  /**
   * Forces a fresh read from server (bypasses the cached in-flight/shared request).
   * Use this after actions that update 2FA session state (e.g., verify OTP).
   */
  reloadFromServer(): Observable<boolean> {
    this.bootstrapRequest$ = undefined;
    return this.loadFromServer();
  }

  /**
   * Sensitive-action rule:
   * - If now < validTill, allow without popup.
   * - Else require popup.
   */
  isValidNow(): boolean {
    const { validTill } = this.snapshot;
    if (!validTill) {
      return false;
    }
    const validTillMs = Date.parse(validTill);
    if (Number.isNaN(validTillMs)) {
      return false;
    }
    return Date.now() < validTillMs;
  }

  /**
   * Gate for sensitive actions.
   * - If 2FA is not enabled (authType is empty string), allow without popup.
   * - If authType is 'disabled', allow for non-whitelisted actions without popup (selective enforcement).
   * - If now < validTill, allow without popup.
   * - Else require popup.
   *
   * NOTE: This depends on UI (MatDialog) by design for easy call-site integration.
   */
  ensureTwoFactorValid(action: TwoFactorAction): Observable<boolean> {
    // 2FA not enabled for the account: never block actions in UI.
    if (this.snapshot.authType === '') {
      return of(true);
    }
    // 2FA selectively enforced: only gate specific actions (whitelist).
    if (this.snapshot.authType === 'disabled' && !this.isWhitelistedAction(action)) {
      return of(true);
    }
    if (this.isValidNow()) {
      return of(true);
    }

    return this.dialog
      .open(TwoFactorAuthDialogComponent, {
        width: '540px',
        maxWidth: '540px',
        disableClose: true,
      })
      .afterClosed()
      .pipe(map((result: boolean | undefined) => Boolean(result)));
  }

  private isWhitelistedAction(action: TwoFactorAction): boolean {
    switch (action) {
      case 'change_password':
      case 'set_report_password':
      case 'delete_account':
      case 'api_key_view':
      case 'api_key_generate':
      case 'api_key_update':
      case 'api_key_disable':
      case 'api_key_delete':
      case 'sms_peid_manage':
      case 'sms_api_password_manage':
      case 'sms_pii_hashing':
      case 'sms_content_storage':
      case 'sms_api_access_security':
      case 'sms_sender_id_management':
      case 'sms_duplicate_check':
      case 'sms_multilingual':
      case 'profile_edit':
      case 'domain_delete':
      case 'smtp_relay_show_password':
      case 'smtp_relay_allowed_ips_edit':
      case 'rcs_recipient_pii_hashing':
        return true;
      default:
        return false;
    }
  }

  setValidTill(validTill: string | null): void {
    this.setStatus({ ...this.snapshot, validTill });
  }

  setAuthType(authType: TwoFactorAuthType): void {
    this.setStatus({ ...this.snapshot, authType });
  }

  private setStatus(next: TwoFactorAuthStatus): void {
    this._status$.next(next);
  }
}


